import * as yup from 'yup';
import { validationMode, validationMessages } from 'helpers/form';
import { RebalanceFrequency } from 'components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkForm';
import mapValues from 'lodash/mapValues';
import { CalculationFrequency } from 'modules/BenchmarkForm';
import { BenchmarkDetailsErrors } from 'helpers/errorMessages';

export const staticWeightedTimeWindowShape = yup.object().shape({
  fromDate: yup
    .date()
    .typeError(validationMessages.required)
    .required(validationMessages.required),
  weightSum: yup.number().equals([100]),
  lineItems: yup
    .array()
    .of(
      yup.object().shape({
        returnComponent: yup.string().required(validationMessages.required),
        weight: yup
          .number()
          .typeError(validationMessages.number)
          .nullable(true)
          .required(validationMessages.required),
      })
    )
    .required()
    .min(1, validationMessages.required),
});

/**
 * Validation Scheme used for Static Weighted Benchmarks
 * @param mode Create | Edit - change the shape of the schema object based on Creating/Editing a benchmark.
 * @returns return yup schema object
 */
export const staticWeightedValidationSchema = (
  mode: validationMode = validationMode.Create
) => {
  return yup.lazy((obj) =>
    yup.object(
      mapValues(obj, (value, key) => {
        switch (key) {
          case 'timeWindows':
            return mode === validationMode.Create
              ? yup.array().of(staticWeightedTimeWindowShape)
              : yup.mixed().notRequired();
          case 'name':
            return yup
              .string()
              .trim()
              .required(validationMessages.benchmarkNameNotEmpty);
          case 'baseCurrencyCode':
            return yup
              .string()
              .typeError(validationMessages.required)
              .required(validationMessages.required);
          case 'calculationFrequencyId':
            return yup
              .number()
              .typeError(validationMessages.required)
              .required(validationMessages.required);
          case 'rebalanceFrequencyId':
            return yup
              .number()
              .typeError(validationMessages.required)
              .required(validationMessages.required);
          case 'rebalanceFrequencyCustomMonths':
            return yup
              .array()
              .when(['rebalanceFrequencyId'], {
                is: (rebalanceFrequencyId: number) => {
                  return rebalanceFrequencyId === RebalanceFrequency.custom;
                },
                then: (schema) =>
                  schema
                    .min(1, validationMessages.required)
                    .required(validationMessages.required),
                otherwise: (schema) => schema.nullable(true).notRequired(),
              })
              .typeError(validationMessages.required);
          case 'customLagDate':
            return yup.string().when(['isLagged', 'setCustomLagDate'], {
              is: (isLagged: string, setCustomLagDate: string) =>
                isLagged === 'true' && setCustomLagDate === 'false',
              then: (schema) => schema.required('Please enter a valid date'),
              otherwise: (schema) => schema.notRequired(),
            });
          case 'pushTimeWindowsToPortfolioAnalytics':
            return yup.boolean().when(['calculationFrequencyId'], {
              is: (calculationFrequencyId: number) => {
                return (
                  value === 'true' &&
                  calculationFrequencyId == CalculationFrequency.day
                );
              },
              then: (schema) => {
                return schema.isFalse(
                  BenchmarkDetailsErrors.cantPushTimeWindowsToPA
                );
              },
              otherwise: (schema) => {
                return schema.notRequired();
              },
            });
          case 'lookThroughDepth':
            return yup
              .number()
              .min(1)
              .max(5)
              .typeError(validationMessages.required)
              .required(validationMessages.required);
          case 'description':
            return yup
              .string()
              .notRequired()
              .max(350, validationMessages.notExceed350);
          default:
            return yup.mixed().notRequired();
        }
      })
    )
  );
};
